import {EScene} from '@/types/scene.types'
import AudioManager from '@repo/shared/AudioManager'
import {useAtomValue} from 'jotai'
import {FC, PropsWithChildren, useEffect, useRef} from 'react'
import {gameModeState, sceneState} from '../Store/atoms'

const audioManager = new AudioManager()

const BGM_MAX_VOLUME = 0.5

export const AudioProvider: FC<PropsWithChildren> = ({children}) => {
  const scene = useAtomValue(sceneState)
  const gameMode = useAtomValue(gameModeState)

  const didRegisterAudio = useRef(false)

  /**
   * Register all audio files
   */
  useEffect(() => {
    if (didRegisterAudio.current) {
      return
    }

    didRegisterAudio.current = true

    audioManager.registerBgm({
      key: 'ingame',
      options: {
        src: 'audio/bgm_skiracev2.mp3',
        loop: true,
        preload: true,
        autoplay: false,
      },
    })

    audioManager.registerSfx({
      key: 'player_join',
      options: {
        src: 'audio/sfx_pop.mp3',
        loop: false,
        preload: true,
      },
    })

    audioManager.registerSfx({
      key: 'cowbells_cheering',
      options: {
        src: 'audio/sfx_cowbells-cheering.mp3',
        loop: true,
        preload: false,
      },
    })

    audioManager.registerSfx({
      key: 'cross_country_skiing',
      options: {
        src: 'audio/sfx_cross-country-skiing.mp3',
        loop: true,
        preload: false,
      },
    })

    audioManager.registerSfx({
      key: 'crowdcheering',
      options: {
        src: 'audio/sfx_crowdcheering.mp3',
        loop: true,
        preload: false,
      },
    })

    audioManager.registerSfx({
      key: 'finish1',
      options: {
        src: 'audio/sfx_finish1.mp3',
        loop: false,
        preload: false,
      },
    })
    audioManager.registerSfx({
      key: 'finish2',
      options: {
        src: 'audio/sfx_finish2.mp3',
        loop: false,
        preload: false,
      },
    })
    audioManager.registerSfx({
      key: 'finish3',
      options: {
        src: 'audio/sfx_finish3.mp3',
        loop: false,
        preload: false,
      },
    })
    audioManager.registerSfx({
      key: 'finish4',
      options: {
        src: 'audio/sfx_finish4.mp3',
        loop: false,
        preload: false,
      },
    })

    audioManager.registerSfx({
      key: 'countdown_tick',
      options: {
        src: 'audio/sfx_countdown-tick.mp3',
        loop: false,
        preload: false,
      },
    })

    audioManager.registerSfx({
      key: 'countdown_tick_final',
      options: {
        src: 'audio/sfx_countdown-tick-final.mp3',
        loop: false,
        preload: false,
      },
    })

    audioManager.registerSfx({
      key: 'starting_pistol',
      options: {
        src: 'audio/sfx_starting-pistol.mp3',
        loop: false,
        preload: false,
      },
    })

    audioManager.registerSfx({
      key: 'confetti',
      options: {
        src: 'audio/sfx_confetti.mp3',
        loop: false,
        preload: false,
      },
    })

    audioManager.registerSfx({
      key: 'clapping',
      options: {
        src: 'audio/sfx_clapping.mp3',
        loop: false,
        preload: false,
      },
    })

    audioManager.registerSfx({
      key: 'button_click',
      options: {
        src: 'audio/sfx_button_click.mp3',
        loop: false,
        preload: true,
      },
    })
  }, [])

  /**
   * Play audio based on scene
   */
  useEffect(() => {
    switch (scene) {
      case EScene.PRE_LOBBY:
        audioManager.resetAllBgm()
        audioManager.resetAllSfx()
        break
      case EScene.LOBBY:
        audioManager.BGM.ingame?.fade(0, BGM_MAX_VOLUME, 1000).play()

        // load sfx
        audioManager.SFX.countdown_tick?.load()
        audioManager.SFX.countdown_tick_final?.load()
        audioManager.SFX.starting_pistol?.load()

        audioManager.SFX.cowbells_cheering?.load()
        audioManager.SFX.cross_country_skiing?.load()
        audioManager.SFX.crowdcheering?.load()
        break

      case EScene.STARTED_COUNTDOWN:
        audioManager.BGM.ingame?.fade(audioManager.BGM.ingame?.volume(), BGM_MAX_VOLUME / 2, 5000)
        break

      case EScene.STARTED:
        audioManager.SFX.cowbells_cheering?.volume(0)
        audioManager.SFX.cowbells_cheering?.seek(0)
        audioManager.SFX.cowbells_cheering?.play()

        audioManager.SFX.crowdcheering?.volume(0)
        audioManager.SFX.crowdcheering?.seek(0)
        audioManager.SFX.crowdcheering?.play()

        // load finish sfx
        audioManager.SFX.finish1?.load()
        audioManager.SFX.finish2?.load()
        audioManager.SFX.finish3?.load()
        audioManager.SFX.finish4?.load()
        audioManager.SFX.confetti?.load()
        audioManager.SFX.clapping?.load()
        break

      case EScene.SCOREBOARD:
        audioManager.BGM.ingame?.fade(audioManager.BGM.ingame.volume(), BGM_MAX_VOLUME, 2000)
        audioManager.SFX.cross_country_skiing?.stop()

        audioManager.SFX.cowbells_cheering
          ?.fade(audioManager.SFX.cowbells_cheering.volume(), 0, 2000)
          .stop()
        audioManager.SFX.crowdcheering
          ?.fade(audioManager.SFX.crowdcheering.volume(), 0, 2000)
          .stop()
        break

      default:
        break
    }
  }, [scene])

  return children
}
